import React, { useState } from "react";
import { Loader2, X } from "lucide-react";
import useRequest from "../../../hook/use-req";
import { toast } from "sonner";

type ModeType = "image" | "text" | "heading" | "button" | "template" | null;
type InsertPosition = "top" | "bottom";

export const VARIABLE_OPTIONS = [
  "first_name",
  "last_name",
  "email",
  "phone",
  "company_name",
  "lead_status",
  "lead_score",
];

const GeneratingOverlay: React.FC<{ mode: ModeType }> = ({ mode }) => {
  const modeLabels: Record<string, string> = {
    image: "Creating your image",
    text: "Writing content",
    heading: "Crafting headline",
    button: "Generating button",
    template: "Building template",
  };

  return (
    <div className="bg-white/95 dark:bg-gray-900/95 flex flex-col items-center justify-center rounded-xl backdrop-blur-sm absolute inset-0">
      <div className="flex gap-2 mb-4">
        <div className="w-2.5 h-2.5 bg-violet-500 rounded-full animate-bounce" />
        <div className="w-2.5 h-2.5 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
        <div className="w-2.5 h-2.5 bg-violet-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
      </div>
      <p className="text-sm font-medium text-slate-700 dark:text-gray-200">
        {modeLabels[mode || "template"] || "Generating"}...
      </p>
    </div>
  );
};

export interface EmailVariable {
  name: string;
  defaultValue: string;
}

interface AIFloatingPanelProps {
  // Default ("Replace with") values per variable — same shape as the
  // template's `variables`. Uncontrolled (kept here) when not passed.
  variables?: EmailVariable[];
  onVariablesChange?: (vars: EmailVariable[]) => void;
  onInsertContent: (
    contentType: string,
    content: string | object,
    position: InsertPosition,
    metadata?: { subject?: string; header?: string; plainText?: string; color?: string }
  ) => void;
}

export const FloatingPanel: React.FC<AIFloatingPanelProps> = ({
  variables: extVariables,
  onVariablesChange,
  onInsertContent,
}) => {
  const [localVariables, setLocalVariables] = useState<EmailVariable[]>([]);
  const variables = extVariables ?? localVariables;
  const setVariables = (vars: EmailVariable[]) => {
    setLocalVariables(vars);
    onVariablesChange?.(vars);
  };
  const [isExpanded, setIsExpanded] = useState(false);
  const [showVariables, setShowVariables] = useState(false);
  const [customVariable, setCustomVariable] = useState("");
  const [activeMode, setActiveMode] = useState<ModeType>(null);
  const [prompt, setPrompt] = useState("");

  const [brandName, setBrandName] = useState("newsletter");
  const [brandVoice, setBrandVoice] = useState("Professional");
  const [personalisationLevel, setPersonalisationLevel] = useState("High (Name + Custom Fields)");
  const [includeImages, setIncludeImages] = useState(true);

  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState("");

  const token = localStorage.getItem("authToken");
  const { makeRequest } = useRequest("/content/email", "POST", {
    Authorization: `Bearer ${token}`,
  });

  // DALL-E Image Generation
  const generateImageWithDalle = async (description: string): Promise<string> => {
    try {
      const apiKey = process.env.REACT_APP_OPENAI_API_KEY || localStorage.getItem("openai_api_key");
      
      if (!apiKey) {
        console.warn("OpenAI API key not found. Using placeholder image.");
        return `https://picsum.photos/id/${Math.floor(Math.random() * 100) + 10}/600/300`;
      }

      const response = await fetch("https://api.openai.com/v1/images/generations", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
        body: JSON.stringify({
          model: "dall-e-3",
          prompt: description,
          n: 1,
          size: "1024x1024",
        }),
      });

      if (!response.ok) throw new Error("Image generation failed");

      const data = await response.json();
      return data.data?.[0]?.url || "https://picsum.photos/600/300";
    } catch (err) {
      console.error("DALL-E error:", err);
      return `https://picsum.photos/id/${Math.floor(Math.random() * 100) + 20}/600/300`;
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError("Please enter a description");
      return;
    }

    setIsGenerating(true);
    setError("");

    try {
      const payload = {
        brand_voice: brandVoice,
        personalisation_level: personalisationLevel,
        message_goal: prompt,
        brand_name: brandName,
        include_images: activeMode === "template" ? includeImages : false,
      };

      const [res] = await makeRequest(payload);

      if (!res?.messages?.[0]) {
        throw new Error(res?.message || "Failed to generate content");
      }

      let html = res.messages[0].html || "<p>No content generated</p>";
      const subject = res.messages[0].subject;
      const plainText = res.messages[0].plain_text;

      // === IMAGE GENERATION LOGIC ===
      if (activeMode === "template" && includeImages) {
        toast.info("Generating beautiful images...");

        // Replace explicit placeholders like GENERATE_IMAGE: description
        const imageMatches = [...html.matchAll(/GENERATE_IMAGE:\s*([^<"]+)/gi)];

        if (imageMatches.length > 0) {
          for (const match of imageMatches) {
            const description = match[1].trim();
            const imageUrl = await generateImageWithDalle(description);

            html = html.replace(
              match[0],
              `<img src="${imageUrl}" style="max-width:100%; height:auto; border-radius:12px; margin:20px 0; box-shadow:0 4px 12px rgba(0,0,0,0.1);" alt="Email visual" />`
            );
          }
        } else {
          // Fallback: Add at least one relevant image at the top
          const fallbackDescription = `${prompt} - professional email hero banner, vibrant, modern marketing style`;
          const imageUrl = await generateImageWithDalle(fallbackDescription);

          html = html.replace(
            /(<h1[^>]*>.*?<\/h1>)/i,
            `$1<img src="${imageUrl}" style="max-width:100%; height:auto; border-radius:12px; margin:20px 0; box-shadow:0 4px 12px rgba(0,0,0,0.1);" alt="Featured image" />`
          );
        }
      }

      onInsertContent("template_html", html, "bottom", {
        subject,
        header: res.messages[0].header,
        plainText,
      });

      toast.success("Full template with images loaded successfully!");
      setPrompt("");
      setActiveMode(null);
    } catch (err: any) {
      setError(err.message || "Generation failed. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClose = () => {
    setIsExpanded(false);
    setShowVariables(false);
    setActiveMode(null);
    setPrompt("");
    setError("");
  };

  const insertVariable = (rawValue: string) => {
    const variableName = rawValue.trim().replace(/[{}]/g, "");
    if (!variableName) return;

    if (!variables.some((v) => v.name === variableName)) setVariables([...variables, { name: variableName, defaultValue: "" }]);
    // The editor copies it for pasting at the cursor (see EmailEditorCore's
    // insertVariable) — the panel stays open so its default can be set.
    onInsertContent("variable", `{{${variableName}}}`, "bottom");
    setCustomVariable("");
  };

  const setDefault = (name: string, defaultValue: string) =>
    setVariables(
      variables.some((v) => v.name === name)
        ? variables.map((v) => (v.name === name ? { ...v, defaultValue } : v))
        : [...variables, { name, defaultValue }]
    );

  // Built-ins first, then any custom ones this template already uses.
  const variableNames = [...VARIABLE_OPTIONS, ...variables.map((v) => v.name).filter((n) => !VARIABLE_OPTIONS.includes(n))];

  if (!isExpanded) {
    return (
      <div className="flex">
        <div className="absolute bottom-5 right-40 ">
          <button
            type="button"
            onClick={() => setShowVariables((open) => !open)}
            className="px-5 py-3 text-sm font-semibold border border-[#D3D5E1] dark:border-gray-700 text-[#3A3428] dark:text-gray-200 bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
          >
            Add Variables
          </button>

          {showVariables && (
            <div className="absolute bottom-full right-0 mb-2 w-[380px] rounded-2xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-2xl overflow-hidden">
              <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-slate-100 dark:border-gray-700">
                <p className="text-sm font-semibold text-slate-800 dark:text-gray-100">Variables</p>
                <button
                  type="button"
                  onClick={() => setShowVariables(false)}
                  aria-label="Close"
                  className="p-1 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:text-gray-400 dark:hover:bg-gray-700"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="px-4 py-3 max-h-52 overflow-y-auto space-y-2">
                <div className="grid grid-cols-[1fr_14px_1fr_20px] gap-2 text-[11px] font-semibold text-slate-400 dark:text-gray-500 uppercase tracking-wide">
                  <span>Variable</span>
                  <span />
                  <span>Replace with</span>
                  <span />
                </div>
                {variableNames.map((name) => {
                  const custom = !VARIABLE_OPTIONS.includes(name);
                  return (
                    <div key={name} className="grid grid-cols-[1fr_14px_1fr_20px] gap-2 items-center">
                      <button
                        type="button"
                        onClick={() => insertVariable(name)}
                        title="Copy to paste at your cursor"
                        className="truncate rounded-lg border border-slate-200 dark:border-gray-700 px-2 py-1.5 text-left font-mono text-xs text-indigo-600 dark:text-indigo-400 hover:border-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-500/10"
                      >
                        {`{{${name}}}`}
                      </button>
                      <span className="text-center text-xs text-slate-400">→</span>
                      <input
                        value={variables.find((v) => v.name === name)?.defaultValue || ""}
                        onChange={(e) => setDefault(name, e.target.value)}
                        placeholder={name === "first_name" ? 'e.g. "Customer"' : "Default value"}
                        className="min-w-0 rounded-lg border border-slate-200 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 px-2.5 py-1.5 text-xs outline-none focus:border-indigo-400"
                      />
                      {custom ? (
                        <button
                          type="button"
                          onClick={() => setVariables(variables.filter((v) => v.name !== name))}
                          className="text-slate-300 hover:text-rose-500 flex items-center justify-center"
                        >
                          <X size={13} />
                        </button>
                      ) : (
                        <span />
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="px-4 pb-4 pt-1 space-y-2">
                <div className="flex gap-2">
                  <input
                    value={customVariable}
                    onChange={(e) => setCustomVariable(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && insertVariable(customVariable)}
                    placeholder="custom_field"
                    className="min-w-0 flex-1 rounded-lg border border-slate-200 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 px-3 py-2 text-xs outline-none focus:border-indigo-400"
                  />
                  <button
                    type="button"
                    onClick={() => insertVariable(customVariable)}
                    className="rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-700"
                  >
                    Add
                  </button>
                </div>
                <p className="text-[11px] leading-relaxed text-slate-400 dark:text-gray-500">
                  Click a variable to copy it, then paste at your cursor — or type <span className="font-mono text-slate-600 dark:text-gray-300">{"{"}</span> in a text block. "Replace with" is used when a contact has no value.
                </p>
              </div>
            </div>
          )}
        </div>

        <button
          className="absolute bottom-5 right-5 px-5 py-3 text-sm font-semibold border border-[#4338CA] dark:border-indigo-400 text-[#4338CA] dark:text-indigo-400 bg-white dark:bg-gray-800 rounded-xl transition-all flex items-center gap-2 "
          onClick={() => setIsExpanded(true)}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M12.5942 23.2584L12.5822 23.2604L12.5112 23.2954L12.4912 23.2994L12.4772 23.2954L12.4062 23.2594C12.3955 23.2568 12.3875 23.2588 12.3822 23.2654L12.3782 23.2754L12.3612 23.7034L12.3662 23.7234L12.3762 23.7364L12.4802 23.8104L12.4952 23.8144L12.5072 23.8104L12.6112 23.7364L12.6232 23.7204L12.6272 23.7034L12.6102 23.2764C12.6075 23.2658 12.6022 23.2598 12.5942 23.2584ZM12.8582 23.1454L12.8442 23.1474L12.6602 23.2404L12.6502 23.2504L12.6472 23.2614L12.6652 23.6914L12.6702 23.7034L12.6782 23.7114L12.8792 23.8034C12.8918 23.8068 12.9015 23.8041 12.9082 23.7954L12.9122 23.7814L12.8782 23.1674C12.8748 23.1548 12.8682 23.1474 12.8582 23.1454ZM12.1432 23.1474C12.1388 23.1448 12.1335 23.1439 12.1285 23.145C12.1234 23.1461 12.119 23.1491 12.1162 23.1534L12.1102 23.1674L12.0762 23.7814C12.0768 23.7934 12.0825 23.8014 12.0932 23.8054L12.1082 23.8034L12.3092 23.7104L12.3192 23.7024L12.3222 23.6914L12.3402 23.2614L12.3372 23.2494L12.3272 23.2394L12.1432 23.1474Z" fill="#4338CA" />
            <path fillRule="evenodd" clipRule="evenodd" d="M12 2C12.901 2 13.774 2.12 14.605 2.344C14.0859 2.61661 13.658 3.03539 13.3743 3.54855C13.0906 4.06172 12.9635 4.64678 13.0087 5.2314C13.0539 5.81602 13.2695 6.37458 13.6287 6.83802C13.9879 7.30145 14.4751 7.64946 15.03 7.839L15.408 7.968C15.5528 8.01735 15.6844 8.09926 15.7926 8.20745C15.9007 8.31563 15.9827 8.44719 16.032 8.592L16.162 8.97C16.3519 9.52433 16.6999 10.0109 17.1631 10.3697C17.6264 10.7285 18.1845 10.9438 18.7687 10.989C19.3529 11.0342 19.9376 10.9073 20.4505 10.6241C20.9634 10.3408 21.3821 9.91353 21.655 9.395C21.8841 10.2444 22.0001 11.1203 22 12C22 17.523 17.523 22 12 22C10.11 22 8.34001 21.475 6.83201 20.563L3.80001 21.454C3.62593 21.5052 3.44126 21.5086 3.26542 21.4638C3.08959 21.4189 2.92908 21.3276 2.80077 21.1992C2.67246 21.0709 2.58108 20.9104 2.53625 20.7346C2.49142 20.5587 2.49479 20.3741 2.54601 20.2L3.43801 17.168C2.49506 15.6093 1.99769 13.8218 2.00001 12C2.00001 6.477 6.47701 2 12 2ZM8.50001 10.5C8.10218 10.5 7.72065 10.658 7.43935 10.9393C7.15804 11.2206 7.00001 11.6022 7.00001 12C7.00001 12.3978 7.15804 12.7794 7.43935 13.0607C7.72065 13.342 8.10218 13.5 8.50001 13.5C8.89783 13.5 9.27936 13.342 9.56067 13.0607C9.84197 12.7794 10 12.3978 10 12C10 11.6022 9.84197 11.2206 9.56067 10.9393C9.27936 10.658 8.89783 10.5 8.50001 10.5ZM15.5 10.5C15.1022 10.5 14.7207 10.658 14.4393 10.9393C14.158 11.2206 14 11.6022 14 12C14 12.3978 14.158 12.7794 14.4393 13.0607C14.7207 13.342 15.1022 13.5 15.5 13.5C15.8978 13.5 16.2794 13.342 16.5607 13.0607C16.842 12.7794 17 12.3978 17 12C17 11.6022 16.842 11.2206 16.5607 10.9393C16.2794 10.658 15.8978 10.5 15.5 10.5ZM19 1C19.2086 1.00008 19.4119 1.06539 19.5816 1.18679C19.7512 1.30818 19.8786 1.47959 19.946 1.677L20.076 2.055C20.376 2.934 21.066 3.625 21.946 3.925L22.323 4.054C22.5202 4.12157 22.6914 4.24908 22.8125 4.41869C22.9337 4.58831 22.9988 4.79155 22.9988 5C22.9988 5.20845 22.9337 5.41169 22.8125 5.58131C22.6914 5.75092 22.5202 5.87843 22.323 5.946L21.945 6.076C21.066 6.376 20.375 7.066 20.075 7.946L19.946 8.323C19.8784 8.5202 19.7509 8.69134 19.5813 8.81252C19.4117 8.93369 19.2085 8.99883 19 8.99883C18.7916 8.99883 18.5883 8.93369 18.4187 8.81252C18.2491 8.69134 18.1216 8.5202 18.054 8.323L17.924 7.945C17.7759 7.51114 17.5303 7.11699 17.2062 6.79283C16.882 6.46866 16.4879 6.2231 16.054 6.075L15.677 5.946C15.4798 5.87843 15.3087 5.75092 15.1875 5.58131C15.0663 5.41169 15.0012 5.20845 15.0012 5C15.0012 4.79155 15.0663 4.58831 15.1875 4.41869C15.3087 4.24908 15.4798 4.12157 15.677 4.054L16.055 3.924C16.934 3.624 17.625 2.934 17.925 2.054L18.054 1.677C18.1214 1.47959 18.2488 1.30818 18.4184 1.18679C18.5881 1.06539 18.7914 1.00008 19 1Z" fill="#4338CA" />
            <path d="M8.51172 15.8539C8.51172 15.8539 9.45199 18.0078 12.075 18.0078C14.698 18.0078 15.5885 15.832 15.5885 15.832" stroke="white" strokeLinecap="round" />
          </svg>
          ConvoAI
        </button>
      </div>
    );
  }

  return (
    <div className="absolute bottom-5 right-5 w-[380px] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl z-50 border border-slate-200 dark:border-gray-700 overflow-hidden">
      {isGenerating && <GeneratingOverlay mode={activeMode} />}

      <div className="px-5 py-4 border-b dark:border-gray-700 flex items-center justify-between bg-white dark:bg-gray-800">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-violet-100 dark:bg-violet-500/15 rounded-xl flex items-center justify-center">✨</div>
          <span className="font-semibold text-lg dark:text-gray-100">AI Assistant</span>
        </div>
        <button onClick={handleClose} className="text-2xl text-slate-400 dark:text-gray-500 hover:text-slate-600 dark:hover:text-gray-300">×</button>
      </div>

      <div className="p-5 space-y-5">
        {activeMode === "template" && (
          <div className="flex items-center gap-3 bg-slate-50 dark:bg-gray-900 p-3 rounded-xl">
            <input
              type="checkbox"
              id="includeImages"
              checked={includeImages}
              onChange={(e) => setIncludeImages(e.target.checked)}
              className="w-4 h-4 accent-violet-600"
            />
            <label htmlFor="includeImages" className="text-sm font-medium text-slate-700 dark:text-gray-200 cursor-pointer">
              Include AI-generated images (DALL-E 3)
            </label>
          </div>
        )}

        {!activeMode ? (
          <div className="grid grid-cols-3 gap-3">
            {[
              { id: "template", icon: "✨", label: "Full Template" },
              { id: "image", icon: "🖼️", label: "Image" },
              { id: "text", icon: "📝", label: "Text" },
              { id: "heading", icon: "🔤", label: "Heading" },
              { id: "button", icon: "🔘", label: "Button" },
            ].map((mode) => (
              <button
                key={mode.id}
                onClick={() => setActiveMode(mode.id as ModeType)}
                className="flex flex-col items-center py-6 px-4 bg-slate-50 dark:bg-gray-900 hover:bg-violet-50 dark:hover:bg-violet-500/10 border border-slate-200 dark:border-gray-700 hover:border-violet-300 dark:hover:border-violet-500/50 rounded-2xl transition-all"
              >
                <span className="text-3xl mb-2">{mode.icon}</span>
                <span className="text-sm font-medium text-slate-700 dark:text-gray-200">{mode.label}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <button onClick={handleClose} className="text-slate-500 dark:text-gray-400">←</button>
              <span className="font-semibold text-lg dark:text-gray-100">
                {["template","image","text","heading","button"].find(m => m === activeMode)}
              </span>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-600 dark:text-gray-400 mb-1">Brand Name</label>
                <select
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  className="w-full p-3 border border-slate-200 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 rounded-xl focus:outline-none focus:border-violet-400"
                >
                  {[
                    { value: "newsletter", label: "Newsletter" },
                    { value: "welcome", label: "Welcome" },
                    { value: "promotional", label: "Promotional" },
                    { value: "win-back", label: "Win-Back" },
                  ].map((brand) => (
                    <option key={brand.value} value={brand.value}>
                      {brand.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 dark:text-gray-400 mb-1">Brand Voice</label>
                <select
                  value={brandVoice}
                  onChange={(e) => setBrandVoice(e.target.value)}
                  className="w-full p-3 border border-slate-200 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 rounded-xl focus:outline-none focus:border-violet-400"
                >
                  <option value="Professional">Professional</option>
                  <option value="Friendly & Casual">Friendly & Casual</option>
                  <option value="Energetic & Fun">Energetic & Fun</option>
                  <option value="Luxury & Sophisticated">Luxury & Sophisticated</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 dark:text-gray-400 mb-1">Personalization Level</label>
                <select
                  value={personalisationLevel}
                  onChange={(e) => setPersonalisationLevel(e.target.value)}
                  className="w-full p-3 border border-slate-200 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 rounded-xl focus:outline-none focus:border-violet-400"
                >
                  <option value="High (Name + Custom Fields)">High (Name + Custom Fields)</option>
                  <option value="Medium (Name + Location)">Medium (Name + Location)</option>
                  <option value="Basic (Name Only)">Basic (Name Only)</option>
                  <option value="No Personalization">No Personalization</option>
                </select>
              </div>
            </div>

            <textarea
              className="w-full h-32 p-4 border border-slate-200 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 rounded-2xl resize-none focus:outline-none focus:border-violet-400"
              placeholder="Describe what you want in this email..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
            />

            {error && <p className="text-red-500 text-sm">{error}</p>}

            <button
              onClick={handleGenerate}
              disabled={isGenerating || !prompt.trim()}
              className="w-full py-3.5 bg-violet-600 hover:bg-violet-700 disabled:bg-gray-400 text-white font-semibold rounded-2xl transition-all flex items-center justify-center gap-2"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generating...
                </>
              ) : (
                "Generate with AI"
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FloatingPanel;